import { useEffect, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import type { AccountLeadPeriod } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  CURRENT_PERIOD_LABEL,
  LEAD_HISTORY_TITLE,
  accountLeadErrorMessage,
  periodLabel,
  sortPeriodsNewestFirst,
} from '@/lib/account-lead-history';
import { AccountLeadPeriodModal } from './AccountLeadPeriodModal';

/*
 * AccountLeadHistorySection: the company page's timeline of account lead
 * periods (owner decisions 1, 5, 7 and 9a, 2026-10-25).
 *
 * Rendered bare: the detail page owns the section chrome (layout id
 * `lead-history`). Reading needs only what `GET /companies/:id` needs; Add /
 * Edit / Delete need `account-lead-history.update` (Management + Super Admin),
 * and the server is the boundary either way.
 *
 * Every write answers with the company's refreshed list, which replaces local
 * state; `onChanged` then lets the page re-read the company, whose current lead
 * the server re-syncs from the history in the same transaction.
 */

export interface AccountLeadHistorySectionProps {
  companyId: number;
  companyName: string;
  /** Bump to force a re-read (e.g. after the page changed the lead). */
  reloadToken?: number;
  /** Called after any successful write. */
  onChanged?: () => void;
}

// `companyId` tags each answer, so a state left from another company reads as loading.
type LoadState =
  | { status: 'loading' }
  | { status: 'error'; companyId: number; message: string }
  | { status: 'ready'; companyId: number; periods: AccountLeadPeriod[] };

function messageOf(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    return accountLeadErrorMessage(caught.code) ?? caught.formMessage ?? caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}

export function AccountLeadHistorySection({
  companyId,
  companyName,
  reloadToken = 0,
  onChanged,
}: AccountLeadHistorySectionProps) {
  const { ability } = useAuth();
  const canEdit = ability.canUpdate('account-lead-history');

  const [rawState, setState] = useState<LoadState>({ status: 'loading' });
  const state: LoadState =
    rawState.status !== 'loading' && rawState.companyId !== companyId ? { status: 'loading' } : rawState;
  const [localReload, setLocalReload] = useState(0);
  const [editing, setEditing] = useState<{ period: AccountLeadPeriod | null } | null>(null);
  const [deleting, setDeleting] = useState<AccountLeadPeriod | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listAccountLeadPeriods(companyId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setState({ status: 'ready', companyId, periods: sortPeriodsNewestFirst(result.data) });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          companyId,
          message: messageOf(caught, 'The account lead history could not be loaded.'),
        });
      });
    return () => controller.abort();
  }, [companyId, reloadToken, localReload]);

  const applyList = (periods: AccountLeadPeriod[]) => {
    setState({ status: 'ready', companyId, periods: sortPeriodsNewestFirst(periods) });
    onChanged?.();
  };

  const confirmDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const result = await api.deleteAccountLeadPeriod(companyId, deleting.id);
      setDeleting(null);
      applyList(result.data);
    } catch (caught) {
      setDeleteError(messageOf(caught, 'The period could not be deleted.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const periods = state.status === 'ready' ? state.periods : [];

  const toCell = (period: AccountLeadPeriod): ReactNode =>
    period.endsOn === null ? (
      <Badge tone="success">{CURRENT_PERIOD_LABEL}</Badge>
    ) : (
      formatDateOnly(period.endsOn)
    );

  const rowActions = (period: AccountLeadPeriod) =>
    canEdit ? (
      <div className="flex justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          tooltip="Edit"
          aria-label={`Edit ${period.user.name} ${periodLabel(period)}`}
          onClick={() => setEditing({ period })}
        >
          <PencilIcon />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          tooltip="Delete"
          aria-label={`Delete ${period.user.name} ${periodLabel(period)}`}
          onClick={() => {
            setDeleteError(null);
            setDeleting(period);
          }}
        >
          <TrashIcon />
        </Button>
      </div>
    ) : null;

  let body: ReactNode;
  if (state.status === 'loading') {
    body = (
      <div className="grid gap-2 p-4" aria-busy="true">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-9 w-full" />
        ))}
      </div>
    );
  } else if (state.status === 'error') {
    body = (
      <div className="p-4">
        <Notice
          tone="danger"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setState({ status: 'loading' });
                setLocalReload((value) => value + 1);
              }}
            >
              Retry
            </Button>
          }
        >
          {state.message}
        </Notice>
      </div>
    );
  } else if (periods.length === 0) {
    body = (
      <EmptyState
        title="No account lead history"
        description={canEdit ? 'Add a period to record who led this company and when.' : undefined}
      />
    );
  } else {
    body = (
      <>
        <div className="max-md:hidden">
          <TableWrap>
            <Table aria-label={`${LEAD_HISTORY_TITLE} for ${companyName}`}>
              <THead>
                <TR>
                  <TH className="min-w-48">Account lead</TH>
                  <TH className="min-w-32">From</TH>
                  <TH className="min-w-32">To</TH>
                  <TH className="min-w-40">Changed by</TH>
                  {canEdit && (
                    <TH>
                      <span className="sr-only">Actions</span>
                    </TH>
                  )}
                </TR>
              </THead>
              <TBody>
                {periods.map((period) => (
                  <TR key={period.id}>
                    <TD className="max-w-80">
                      <span className="font-medium text-fg [overflow-wrap:anywhere]">{period.user.name}</span>
                    </TD>
                    <TD className="tabular-nums">{formatDateOnly(period.startsOn)}</TD>
                    <TD className="tabular-nums">{toCell(period)}</TD>
                    <TD className="max-w-60 [overflow-wrap:anywhere]">{period.createdBy?.name ?? '—'}</TD>
                    {canEdit && <TD className="text-right">{rowActions(period)}</TD>}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>

        <ul className="m-0 list-none p-0 md:hidden">
          {periods.map((period) => (
            <li key={period.id} className="border-b border-border px-4 py-3 last:border-b-0">
              <div className="flex items-start justify-between gap-2">
                <p className="m-0 min-w-0 text-sm font-semibold text-fg [overflow-wrap:anywhere]">
                  {period.user.name}
                </p>
                {rowActions(period)}
              </div>
              <dl className="m-0 mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                <dt className="text-fg-muted">From</dt>
                <dd className="m-0 tabular-nums">{formatDateOnly(period.startsOn)}</dd>
                <dt className="text-fg-muted">To</dt>
                <dd className="m-0 tabular-nums">{toCell(period)}</dd>
                <dt className="text-fg-muted">Changed by</dt>
                <dd className="m-0 min-w-0 [overflow-wrap:anywhere]">{period.createdBy?.name ?? '—'}</dd>
              </dl>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="min-w-0">
      {canEdit && (
        <div className="flex justify-end px-4 pt-3 pb-2">
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<PlusIcon />}
            disabled={state.status !== 'ready'}
            onClick={() => setEditing({ period: null })}
          >
            Add period
          </Button>
        </div>
      )}

      {body}

      <AccountLeadPeriodModal
        open={editing !== null}
        companyId={companyId}
        companyName={companyName}
        period={editing?.period ?? null}
        periods={periods}
        onClose={() => setEditing(null)}
        onSaved={(next) => {
          setEditing(null);
          applyList(next);
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Delete account lead period"
        description={
          deleting ? (
            <>
              Delete {deleting.user.name}&rsquo;s period {periodLabel(deleting)} for {companyName}? Targets and
              commission credit for this period will move.
            </>
          ) : null
        }
        confirmLabel="Delete"
        tone="danger"
        loading={deleteBusy}
        error={deleteError}
      />
    </div>
  );
}
